import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Maximize2,
  PanelRight,
  X,
} from "lucide-react";
import { SproutIcon } from "@/components/icons/SproutIcon";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useOnboarding } from "@/hooks/use-onboarding";
import { HELP_IDS } from "@/lib/help-ids";
import { cn } from "@/lib/utils";
import { useChatStore } from "@/stores/chat-store";
import type { HelpFlow, HelpFlowTone, HelpStep } from "./help-flows";
import { HELP_FLOWS } from "./help-flows";
import { FLOW_ICON_MAP, HELP_TONE_STYLES } from "./illustrations";
import { ScrollContainerContext } from "./scroll-spotlight";

const MOTION_TRANSITION = { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const };

const HELP_SUGGESTIONS: Record<string, { label: string; value: string }> = {
  "cli-setup": {
    label: "Help with environment setup",
    value: "Can you help me finish the SpecTree environment setup in GitHub Copilot CLI?",
  },
  "create-request": {
    label: "Help me draft a request",
    value: "Can you help me create a strong Epic Request in SpecTree?",
  },
  "plan-epic": {
    label: "Help me plan an epic",
    value: "Can you help me turn my Epic Request into a well-structured SpecTree plan?",
  },
  execute: {
    label: "Help me run Orchestrator",
    value: "Can you help me execute an epic with the SpecTree orchestrator?",
  },
  review: {
    label: "Help me review output",
    value: "Can you help me review and validate work generated by SpecTree?",
  },
};

type ProgressStepState = "completed" | "current" | "available" | "locked";

interface ProgressStep {
  id: string;
  state: ProgressStepState;
}

function areStepPrerequisitesMet(
  step: HelpStep,
  completedStepIds: ReadonlySet<string>,
): boolean {
  return step.prerequisites?.every((stepId) => completedStepIds.has(stepId)) ?? true;
}

function isStepSelectable(
  step: HelpStep,
  completedStepIds: ReadonlySet<string>,
): boolean {
  return completedStepIds.has(step.id) || areStepPrerequisitesMet(step, completedStepIds);
}

function getFlowResumeStep(
  flow: HelpFlow,
  completedStepIds: ReadonlySet<string>,
): number {
  const nextUnlockedStepIndex = flow.steps.findIndex(
    (step) =>
      !completedStepIds.has(step.id) &&
      areStepPrerequisitesMet(step, completedStepIds),
  );

  if (nextUnlockedStepIndex !== -1) {
    return nextUnlockedStepIndex;
  }

  const firstIncompleteStepIndex = flow.steps.findIndex(
    (step) => !completedStepIds.has(step.id),
  );

  return firstIncompleteStepIndex === -1 ? 0 : firstIncompleteStepIndex;
}

function FlowIcon({ name, className }: { name: string; className?: string }) {
  const Icon = FLOW_ICON_MAP[name];

  if (!Icon) {
    return null;
  }

  return <Icon className={className} />;
}

/* -------------------------------------------------------------------------- */
/*  TopBar — changes between menu and wizard mode                             */
/* -------------------------------------------------------------------------- */

function TopBar({
  mode,
  docked,
  onReturnToLaunchpad,
  onToggleDock,
  onClose,
}: {
  mode: "menu" | "wizard";
  docked: boolean;
  onReturnToLaunchpad: () => void;
  onToggleDock: () => void;
  onClose: () => void;
}) {
  return (
    <div className={cn("top-0 z-30", docked ? "absolute left-0 right-0" : "fixed left-0 right-0")}>
      {/* Dark frosted gradient — tinted blur at top, fading to transparent */}
      <div
        className="pointer-events-none absolute inset-0 h-24"
        style={{
          background: "linear-gradient(to bottom, rgba(9,9,11,0.85) 0%, rgba(9,9,11,0) 100%)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          maskImage: "linear-gradient(to bottom, black 30%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 30%, transparent 100%)",
        }}
      />
      <div className={cn("relative flex h-14 items-center justify-between", docked ? "px-4" : "px-6 sm:px-8")}>
      <div className="flex min-w-0 items-center gap-2.5" data-help-id={HELP_IDS.topbar.logo}>
        <img src="/toro-logo.svg" alt="SpecTree" className="h-7 shrink-0" />
        {!docked && (
          <span className="text-base font-semibold text-foreground" style={{ fontFamily: "'Golos Text', sans-serif" }}>
            SpecTree
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        {mode === "wizard" && !docked ? (
          <button
            type="button"
            onClick={onReturnToLaunchpad}
            className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-white/[0.06]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Return to Help Center</span>
          </button>
        ) : mode === "wizard" && docked ? (
          <button
            type="button"
            onClick={onReturnToLaunchpad}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground hover:bg-white/[0.06]"
            aria-label="Return to Help Center"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
        ) : null}
        {/* Dock / Undock toggle */}
        <div className="flex items-center rounded-lg border border-white/[0.08] p-0.5">
          <button
            type="button"
            onClick={() => { if (docked) onToggleDock(); }}
            aria-label="Fullscreen mode"
            className={cn(
              "h-6 w-6 rounded-md flex items-center justify-center transition-colors",
              !docked
                ? "bg-white/[0.12] text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Maximize2 className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => { if (!docked) onToggleDock(); }}
            aria-label="Dock to right panel"
            className={cn(
              "h-6 w-6 rounded-md flex items-center justify-center transition-colors",
              docked
                ? "bg-white/[0.12] text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <PanelRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={cn(
            "flex items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.03] text-foreground transition-colors hover:bg-white/[0.06]",
            docked ? "h-8 w-8" : "h-10 w-10"
          )}
          aria-label="Close Help Center"
        >
          <X className={docked ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </button>
      </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  ProgressBar                                                               */
/* -------------------------------------------------------------------------- */

function ProgressBar({
  totalSteps,
  currentStep,
  flowBreaks,
  onSelect,
  stepStates,
}: {
  totalSteps: number;
  currentStep: number;
  flowBreaks?: number[] | undefined;
  onSelect?: ((index: number) => void) | undefined;
  stepStates?: ProgressStep[] | undefined;
}) {
  const breakSet = useMemo(() => new Set(flowBreaks ?? []), [flowBreaks]);
  const renderedSteps =
    stepStates ??
    Array.from({ length: totalSteps }, (_, index) => ({
      id: `step-${String(index)}`,
      state:
        index === currentStep
          ? ("current" as const)
          : index < currentStep
            ? ("completed" as const)
            : ("available" as const),
    }));

  return (
    <div
      className="flex flex-1 items-center gap-1.5"
      data-help-id={HELP_IDS.helpCenter.stepIndicator}
    >
      {renderedSteps.map((progressStep, index) => (
        <span
          key={`${progressStep.id}-${String(index)}`}
          className={cn("flex flex-1", breakSet.has(index) && "ml-1.5")}
        >
          <button
            type="button"
            onClick={() => {
              if (progressStep.state !== "locked") {
                onSelect?.(index);
              }
            }}
            disabled={progressStep.state === "locked"}
            className={cn(
              "flex h-6 w-full items-center justify-center transition-colors",
              progressStep.state === "locked" &&
                "cursor-not-allowed opacity-60",
            )}
            aria-current={index === currentStep ? "step" : undefined}
            aria-label={
              progressStep.state === "locked"
                ? `Step ${String(index + 1)} is locked`
                : `Go to step ${String(index + 1)}`
            }
          >
            <span
              className={cn(
                "h-1.5 w-full rounded-full transition-colors",
                progressStep.state === "completed"
                  ? "bg-white"
                  : progressStep.state === "current"
                    ? "bg-white/80"
                    : progressStep.state === "locked"
                      ? "bg-white/[0.12]"
                      : "bg-white/[0.18] hover:bg-white/[0.28]",
              )}
            />
          </button>
        </span>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  BottomBar                                                                 */
/* -------------------------------------------------------------------------- */

function BottomBar({
  onBack,
  onNext,
  onSelectStep,
  isLast,
  totalSteps,
  currentStep,
  flowBreaks,
  stepStates,
  docked,
}: {
  onBack: () => void;
  onNext: () => void;
  onSelectStep: (index: number) => void;
  isLast: boolean;
  totalSteps: number;
  currentStep: number;
  flowBreaks?: number[] | undefined;
  stepStates?: ProgressStep[] | undefined;
  docked?: boolean | undefined;
}) {
  return (
    <div className={cn(
      "bottom-0 left-0 right-0 z-30 border-t border-white/[0.06] bg-white/[0.03] backdrop-blur-xl",
      docked ? "absolute rounded-b-2xl" : "fixed"
    )}>
      <div className={cn("flex items-center", docked ? "h-16 gap-4 px-4" : "h-20 gap-6 px-8")}>
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-semibold text-foreground underline underline-offset-4 transition-colors hover:text-foreground/80"
        >
          Back
        </button>

        <ProgressBar
          totalSteps={totalSteps}
          currentStep={currentStep}
          flowBreaks={flowBreaks}
          onSelect={onSelectStep}
          stepStates={stepStates}
        />

        <button
          type="button"
          onClick={onNext}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90",
            docked ? "px-4 py-2.5 text-xs" : "px-6 py-3 text-sm"
          )}
        >
          <span>{isLast ? "Finish" : "Next"}</span>
          {!isLast ? <ArrowRight className={docked ? "h-3.5 w-3.5" : "h-4 w-4"} /> : null}
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  FlowCard                                                                  */
/* -------------------------------------------------------------------------- */

function FlowCard({
  flow,
  completedSteps,
  isComplete,
  onSelect,
}: {
  flow: HelpFlow;
  completedSteps: number;
  isComplete: boolean;
  onSelect: (flowId: string) => void;
}) {
  const styles = HELP_TONE_STYLES[flow.tone];
  const statusLabel = isComplete ? "Complete" : completedSteps > 0 ? "Continue" : "New";
  return (
    <button
      type="button"
      onClick={() => {
        onSelect(flow.id);
      }}
      data-help-id={HELP_IDS.helpCenter.flowCard}
      className={cn(
        "group flex h-full w-full flex-col rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-left transition-all hover:border-white/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/[0.12] focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        styles.ring,
        `hover:bg-gradient-to-br ${styles.hero}`
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl border border-white/[0.08]", styles.icon)}>
          <FlowIcon name={flow.icon} className="h-5 w-5" />
        </div>
        <span className="rounded-full border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] font-medium text-foreground/75">
          {statusLabel}
        </span>
      </div>

      <div className="mt-5 space-y-2">
        <h3 className="text-lg font-semibold text-foreground">{flow.title}</h3>
        <p className="text-sm leading-6 text-muted-foreground">{flow.description}</p>
      </div>

      <div className="mt-auto flex items-end justify-between gap-4 pt-6">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/80">
            {flow.estimatedTime} • {String(flow.stepCount)} steps
          </p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground/80" />
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  MenuView — Help Center homepage with Guides/Workflows tabs                  */
/* -------------------------------------------------------------------------- */


const TONE_HUE: Record<string, string> = {
  violet: "#8B5CF6",
  blue: "#3B82F6",
  emerald: "#22C55E",
  amber: "#EAB308",
  rose: "#EF4444",
};

const WORKFLOWS = [
  {
    id: "full-setup-to-execution",
    title: "Complete Journey",
    description: "From first install all the way to your first executed epic. The full end-to-end experience.",
    flowIds: ["cli-setup", "create-request", "plan-epic", "execute", "review"],
    tone: "violet" as HelpFlowTone,
    badge: "Start here",
  },
  {
    id: "idea-to-execution",
    title: "Idea \u2192 Execution",
    description: "Already set up? Jump straight from creating a request to planning and executing your first epic.",
    flowIds: ["create-request", "plan-epic", "execute"],
    tone: "blue" as HelpFlowTone,
    badge: "Already set up",
  },
  {
    id: "execute-and-review",
    title: "Execute & Review",
    description: "You have a plan ready. Learn how to run the Orchestrator and review the output.",
    flowIds: ["execute", "review"],
    tone: "amber" as HelpFlowTone,
    badge: "Have a plan",
  },
  {
    id: "ship-and-polish",
    title: "Ship & Polish",
    description: "After execution, use Designer for pixel-perfect UI and PullRequest for automated code review before merging.",
    flowIds: ["designer", "pullrequest"],
    tone: "emerald" as HelpFlowTone,
    badge: "Post-execution",
  },
  {
    id: "fogbugz-integration",
    title: "FogBugz Integration",
    description: "Bridge bugs from FogBugz into SpecTree, file new cases, and inspect cross-system status.",
    flowIds: ["bug-bridge", "fogbugz-formulator", "fogbugz-inspector"],
    tone: "rose" as HelpFlowTone,
    badge: "Bug tracking",
  },
];

function MenuView({
  flows,
  completedCount,
  getCompletedSteps,
  isFlowComplete,
  onSelectFlow,
  onSelectWorkflow,
  docked,
}: {
  flows: HelpFlow[];
  completedCount: number;
  getCompletedSteps: (flow: HelpFlow) => number;
  isFlowComplete: (id: string) => boolean;
  onSelectFlow: (flowId: string) => void;
  onSelectWorkflow: (workflowId: string) => void;
  docked?: boolean | undefined;
}) {
  const [activeTab, setActiveTab] = useState<"guides" | "workflows">("guides");
  const nextFlow = flows.find((flow) => !isFlowComplete(flow.id)) ?? null;
  const nextFlowStyles = nextFlow ? HELP_TONE_STYLES[nextFlow.tone] : null;

  return (
    <div className={cn("mx-auto max-w-7xl", docked ? "px-4 py-8" : "px-8 py-14 sm:py-20")} data-help-id={HELP_IDS.helpCenter.menu}>
      {/* ---- Hero — left aligned ---- */}
      <div>
        <h1 className={cn("font-semibold tracking-tight text-foreground", docked ? "text-2xl" : "text-4xl sm:text-5xl lg:text-[3.5rem]")}>
          Help Center
        </h1>
        <p className={cn("mt-4 max-w-2xl leading-8 text-muted-foreground", docked ? "text-sm leading-6" : "text-lg")}>
          Step-by-step guidance for setup, planning, execution, and review — designed to keep the next move obvious.
        </p>
      </div>

      {/* ---- Continue card — unified ---- */}
      {nextFlow ? (
        <button
          type="button"
          onClick={() => {
            onSelectFlow(nextFlow.id);
          }}
          className={cn(
            "mt-10 flex w-full items-center gap-6 rounded-2xl border p-6 text-left transition-all",
            docked && "gap-4 p-4",
            "border-white/[0.08] bg-gradient-to-r hover:border-white/[0.14]",
            nextFlowStyles?.hero,
            "bg-white/[0.02] hover:bg-white/[0.04]"
          )}
        >
          <div
            className={cn(
              "flex shrink-0 items-center justify-center rounded-xl border border-white/[0.08]",
              docked ? "h-10 w-10" : "h-14 w-14",
              nextFlowStyles?.icon
            )}
          >
            <FlowIcon name={nextFlow.icon} className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className={cn("text-xs font-semibold uppercase tracking-[0.18em]", nextFlowStyles?.muted)}>
              Continue where you left off
            </p>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-foreground">{nextFlow.title}</h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground line-clamp-1">{nextFlow.description}</p>
          </div>
          {!docked && (
            <div className="flex items-center gap-5">
              <div className="text-right">
                <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground/70">Step</p>
                <p className="mt-1 text-lg font-semibold text-foreground">
                  {String(getCompletedSteps(nextFlow))}<span className="text-muted-foreground/50">/{String(nextFlow.steps.length)}</span>
                </p>
              </div>
              <div className={cn("flex h-12 w-12 items-center justify-center rounded-full", nextFlowStyles?.icon)}>
                <ArrowRight className="h-5 w-5" />
              </div>
            </div>
          )}
        </button>
      ) : (
        <div className="mt-10 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg font-semibold text-foreground">All guides complete!</p>
              <p className="mt-0.5 text-sm text-muted-foreground">You can revisit any guide below anytime you need a refresher.</p>
            </div>
          </div>
        </div>
      )}

      {/* ---- Tabs: Guides / Workflows ---- */}
      <div className="mt-14">
        <div className="flex items-end justify-between gap-6 border-b border-white/[0.06]">
          <div className="flex">
            <button
              type="button"
              onClick={() => {
                setActiveTab("guides");
              }}
              className={cn(
                "relative px-5 pb-3.5 text-sm font-semibold transition-colors",
                activeTab === "guides"
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground/80"
              )}
            >
              Guides
              {activeTab === "guides" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary" />
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("workflows");
              }}
              className={cn(
                "relative px-5 pb-3.5 text-sm font-semibold transition-colors",
                activeTab === "workflows"
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground/80"
              )}
            >
              Workflows
              {activeTab === "workflows" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary" />
              )}
            </button>
          </div>
          <p className="hidden pb-3.5 text-sm text-muted-foreground/70 sm:block">
            {activeTab === "guides"
              ? `${String(flows.length)} guides • ${String(completedCount)} complete`
              : `${String(WORKFLOWS.length)} workflows`}
          </p>
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          {activeTab === "guides" ? (
            <motion.div
              key="guides-tab"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className={cn("mt-8 grid gap-4", docked ? "grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4")}
            >
              {flows.map((flow) => (
                <FlowCard
                  key={flow.id}
                  flow={flow}
                  completedSteps={getCompletedSteps(flow)}
                  isComplete={isFlowComplete(flow.id)}
                  onSelect={onSelectFlow}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="workflows-tab"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="mt-8 space-y-4"
            >
              {WORKFLOWS.map((workflow) => {
                const wfFlows = workflow.flowIds
                  .map((id) => flows.find((f) => f.id === id))
                  .filter(Boolean) as HelpFlow[];
                const wfStyles = HELP_TONE_STYLES[workflow.tone];
                const totalSteps = wfFlows.reduce((sum, f) => sum + f.steps.length, 0);
                const completedSteps = wfFlows.reduce((sum, f) => sum + getCompletedSteps(f), 0);
                const allDone = wfFlows.every((f) => isFlowComplete(f.id));

                return (
                  <button
                    key={workflow.id}
                    type="button"
                    onClick={() => {
                      onSelectWorkflow(workflow.id);
                    }}
                    className={cn(
                      "group flex w-full items-start gap-6 rounded-2xl border p-6 text-left transition-all",
                      "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]",
                      wfStyles.ring
                    )}
                  >
                    <div className={cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-white/[0.08]", wfStyles.icon)}>
                      <ArrowRight className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-foreground">{workflow.title}</h3>
                        <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-medium", wfStyles.badge)}>
                          {workflow.badge}
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{workflow.description}</p>
                      <div className="mt-4 flex items-center gap-1.5 overflow-x-auto">
                        {wfFlows.map((f, i) => {
                          const fDone = isFlowComplete(f.id);
                          return (
                            <span key={f.id} className="flex shrink-0 items-center gap-1.5">
                              {i > 0 && (
                                <span className="h-px w-3 bg-white/[0.12]" />
                              )}
                              <span className={cn(
                                "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-medium",
                                fDone
                                  ? "bg-emerald-500/10 text-emerald-400"
                                  : "bg-white/[0.04] text-muted-foreground"
                              )}>
                                {fDone ? <CheckCircle2 className="h-3 w-3" /> : (
                                  <span className="text-[10px] text-muted-foreground/50">{String(i + 1)}</span>
                                )}
                                {f.title}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    {!docked && (
                      <div className="flex flex-col items-end gap-2">
                        <p className="text-sm font-semibold text-foreground">
                          {String(completedSteps)}<span className="text-muted-foreground/50">/{String(totalSteps)}</span>
                        </p>
                        <p className="text-xs text-muted-foreground/70">
                          {allDone ? "Complete" : `${String(wfFlows.length)} guides`}
                        </p>
                      </div>
                    )}
                  </button>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Sprout CTA — shown on the last step of every wizard                       */
/* -------------------------------------------------------------------------- */

function SproutEndCTA({ onAskSprout }: { onAskSprout: () => void }) {
  return (
    <div className="mt-12 rounded-xl border border-white/[0.08] bg-white/[0.03] p-6">
      <div className="flex items-start gap-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-emerald-500/10 text-emerald-400">
          <SproutIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-foreground">Need more help?</h3>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Sprout can answer questions, walk you through edge cases, or pick up right where this guide leaves off.
          </p>
          <button
            type="button"
            onClick={onAskSprout}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white/[0.06] px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.1]"
          >
            <SproutIcon className="h-4 w-4" />
            Talk to Sprout
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  WizardContent                                                             */
/* -------------------------------------------------------------------------- */

function WizardContent({
  flow,
  step,
  activeStep,
  totalSteps,
  workflowLabel,
  onAdvance,
  onAskSprout,
  onNavigateToFlow,
  docked,
}: {
  flow: HelpFlow;
  step: HelpStep;
  activeStep: number;
  totalSteps: number;
  workflowLabel?: string | undefined;
  onAdvance: () => void;
  onAskSprout: () => void;
  onNavigateToFlow: (flowId: string) => void;
  docked?: boolean | undefined;
}) {
  const StepComponent = step.component;
  const styles = HELP_TONE_STYLES[flow.tone];
  const isLastStep = activeStep === totalSteps - 1;

  return (
    <div className={cn("mx-auto max-w-7xl", docked ? "px-4 py-6" : "px-8 py-12 sm:py-16")} data-help-id={HELP_IDS.helpCenter.wizard}>
      <AnimatePresence mode="wait">
        <motion.div
          key={`${flow.id}-${step.id}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={MOTION_TRANSITION}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1 text-xs font-semibold text-foreground/85">
              <span className={cn("h-1.5 w-1.5 rounded-full", styles.progress)} />
              {flow.title}
            </span>
            <span className="text-sm text-muted-foreground">
              Step {String(activeStep + 1)} of {String(totalSteps)}
            </span>

            {workflowLabel ? (
              <span className="rounded-md bg-white/[0.04] px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {workflowLabel}
              </span>
            ) : null}
          </div>

          <h1 className={cn("mt-6 font-semibold tracking-tight text-foreground", docked ? "text-xl" : "text-3xl sm:text-4xl")}>
            {step.title}
          </h1>
          <p className={cn("max-w-2xl text-muted-foreground", docked ? "mt-3 text-sm leading-6" : "mt-4 text-lg leading-8")}>{step.description}</p>

          <div className={cn("mt-10", docked && "docked-step-content")}>
            <StepComponent
              flow={flow}
              step={step}
              stepIndex={activeStep}
              totalSteps={totalSteps}
              onAdvance={onAdvance}
              onNavigateToFlow={onNavigateToFlow}
            />
          </div>

          {isLastStep ? <SproutEndCTA onAskSprout={onAskSprout} /> : null}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  HelpCenterModal (root)                                                    */
/* -------------------------------------------------------------------------- */

export function HelpCenterModal() {
  const closeHelpCenter = useChatStore((store) => store.closeHelpCenter);
  const helpCenterFlowId = useChatStore((store) => store.helpCenterFlowId);
  const helpCenterMode = useChatStore((store) => store.helpCenterMode);
  const isHelpCenterOpen = useChatStore((store) => store.isHelpCenterOpen);
  const helpCenterDockMode = useChatStore((store) => store.helpCenterDockMode);
  const onboardingProgress = useChatStore((store) => store.onboardingProgress);
  const setHelpCenterDockMode = useChatStore((store) => store.setHelpCenterDockMode);
  const setShowHelpTooltip = useChatStore((store) => store.setShowHelpTooltip);
  const openPanel = useChatStore((store) => store.openPanel);
  const setPendingSuggestions = useChatStore((store) => store.setPendingSuggestions);
  const {
    completedSteps,
    isFlowComplete,
    isNewUser,
    markFlowComplete,
    markStepComplete,
    markWizardComplete,
    onboardingStage,
    setWizardStep,
    updateOnboardingProgress,
  } = useOnboarding();

  const [activeView, setActiveView] = useState<"menu" | "wizard">("menu");
  const [activeFlowId, setActiveFlowId] = useState<string | null>(null);
  const [activeWorkflowId, setActiveWorkflowId] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current !== null) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const activeFlow = useMemo(
    () => HELP_FLOWS.find((flow) => flow.id === activeFlowId) ?? null,
    [activeFlowId]
  );
  const step = activeFlow?.steps[activeStep] ?? null;
  const isLast = activeFlow ? activeStep === activeFlow.steps.length - 1 : false;

  const activeWorkflow = useMemo(
    () => (activeWorkflowId ? WORKFLOWS.find((w) => w.id === activeWorkflowId) ?? null : null),
    [activeWorkflowId]
  );
  const activeFlowHue = activeFlow ? (TONE_HUE[activeFlow.tone] ?? "") : "";
  const completedStepSet = useMemo(() => new Set(completedSteps), [completedSteps]);

  const workflowFlows = useMemo(() => {
    if (!activeWorkflow) return [];
    return activeWorkflow.flowIds
      .map((id) => HELP_FLOWS.find((fl) => fl.id === id))
      .filter(Boolean) as HelpFlow[];
  }, [activeWorkflow]);

  const workflowTotalSteps = useMemo(
    () => workflowFlows.reduce((sum, fl) => sum + fl.steps.length, 0),
    [workflowFlows]
  );

  const workflowCurrentStep = useMemo(() => {
    if (!activeWorkflow || !activeFlowId) return 0;
    let offset = 0;
    for (const fl of workflowFlows) {
      if (fl.id === activeFlowId) return offset + activeStep;
      offset += fl.steps.length;
    }
    return offset;
  }, [activeWorkflow, activeFlowId, activeStep, workflowFlows]);

  const workflowFlowBreaks = useMemo(() => {
    if (!activeWorkflow) return undefined;
    const breaks: number[] = [];
    let offset = 0;
    for (const fl of workflowFlows) {
      if (offset > 0) breaks.push(offset);
      offset += fl.steps.length;
    }
    return breaks;
  }, [activeWorkflow, workflowFlows]);

  const isWorkflowLast = useMemo(() => {
    if (!activeWorkflow || !activeFlowId) return false;
    const lastFlow = workflowFlows[workflowFlows.length - 1];
    return activeFlowId === lastFlow?.id && isLast;
  }, [activeWorkflow, activeFlowId, isLast, workflowFlows]);

  const navigationSteps = useMemo(
    () => (activeWorkflow ? workflowFlows.flatMap((flow) => flow.steps) : activeFlow?.steps ?? []),
    [activeFlow, activeWorkflow, workflowFlows]
  );

  const progressStepStates = useMemo(() => {
    const currentIndex = activeWorkflow ? workflowCurrentStep : activeStep;

    return navigationSteps.map((candidate, index) => {
      let state: ProgressStepState;

      if (index === currentIndex) {
        state = "current";
      } else if (index < currentIndex && completedStepSet.has(candidate.id)) {
        state = "completed";
      } else if (areStepPrerequisitesMet(candidate, completedStepSet)) {
        state = "available";
      } else {
        state = "locked";
      }

      return { id: candidate.id, state };
    });
  }, [activeStep, activeWorkflow, completedStepSet, navigationSteps, workflowCurrentStep]);

  useEffect(() => {
    if (helpCenterMode === "wizard" && helpCenterFlowId) {
      const flow = HELP_FLOWS.find((candidate) => candidate.id === helpCenterFlowId);
      const savedCompletedSteps = new Set(
        useChatStore.getState().onboardingProgress.completedSteps,
      );

      setActiveWorkflowId(null);
      setActiveFlowId(helpCenterFlowId);
      setActiveView("wizard");
      setActiveStep(flow ? getFlowResumeStep(flow, savedCompletedSteps) : 0);
      return;
    }

    if (helpCenterMode === "menu") {
      setActiveView("menu");
      setActiveFlowId(null);
      setActiveWorkflowId(null);
      setActiveStep(0);
    }
  }, [helpCenterFlowId, helpCenterMode]);

  useEffect(() => {
    if (activeView !== "wizard" || !activeFlow) {
      return;
    }

    const currentStep = activeFlow.steps[activeStep];
    if (!currentStep) {
      setActiveStep(getFlowResumeStep(activeFlow, completedStepSet));
      return;
    }

    if (!isStepSelectable(currentStep, completedStepSet)) {
      setActiveStep(getFlowResumeStep(activeFlow, completedStepSet));
    }
  }, [activeFlow, activeStep, activeView, completedStepSet]);

  useEffect(() => {
    if (activeView !== "wizard" || !activeFlowId) {
      return;
    }

    setWizardStep(activeStep);
  }, [activeFlowId, activeStep, activeView, setWizardStep]);

  // Auto-scroll to top when wizard step changes
  useEffect(() => {
    if (activeView === "wizard" && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeStep, activeView]);

  const completedCount = useMemo(
    () => HELP_FLOWS.filter((flow) => isFlowComplete(flow.id)).length,
    [isFlowComplete]
  );

  const getCompletedSteps = useCallback(
    (flow: HelpFlow) => {
      if (isFlowComplete(flow.id)) {
        return flow.steps.length;
      }

      return flow.steps.filter(({ id }) => completedStepSet.has(id)).length;
    },
    [completedStepSet, isFlowComplete]
  );

  const handleSelectFlow = useCallback(
    (flowId: string) => {
      const flow = HELP_FLOWS.find((item) => item.id === flowId);
      if (!flow) {
        return;
      }

      const resumeStep = isFlowComplete(flowId)
        ? 0
        : getFlowResumeStep(flow, completedStepSet);

      setActiveWorkflowId(null);
      setActiveFlowId(flowId);
      setActiveStep(resumeStep);
      setActiveView("wizard");
    },
    [completedStepSet, isFlowComplete]
  );

  const handleSelectWorkflow = useCallback(
    (workflowId: string) => {
      const workflow = WORKFLOWS.find((w) => w.id === workflowId);
      if (!workflow) return;
      const wfFlows = workflow.flowIds
        .map((id) => HELP_FLOWS.find((fl) => fl.id === id))
        .filter(Boolean) as HelpFlow[];
      if (wfFlows.length === 0) return;

      const firstIncomplete = wfFlows.find((fl) => !isFlowComplete(fl.id));
      const targetFlow = firstIncomplete ?? wfFlows[0];
      if (!targetFlow) return;

      const resumeStep = isFlowComplete(targetFlow.id)
        ? 0
        : getFlowResumeStep(targetFlow, completedStepSet);

      setActiveWorkflowId(workflowId);
      setActiveFlowId(targetFlow.id);
      setActiveStep(resumeStep);
      setActiveView("wizard");
    },
    [completedStepSet, isFlowComplete]
  );

  const handleBackToMenu = useCallback(() => {
    setActiveView("menu");
    setActiveFlowId(null);
    setActiveWorkflowId(null);
  }, []);

  const handleClose = useCallback(() => {
    if (isNewUser && !onboardingProgress.dismissed) {
      updateOnboardingProgress({ dismissed: true });

      if (!onboardingProgress.hasSeenHelpTooltip) {
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltipTimeoutRef.current = null;
          if (!useChatStore.getState().isHelpCenterOpen) {
            setShowHelpTooltip(true);
          }
        }, 300);
      }
    }

    closeHelpCenter();
  }, [closeHelpCenter, isNewUser, onboardingProgress.dismissed, onboardingProgress.hasSeenHelpTooltip, setShowHelpTooltip, updateOnboardingProgress]);

  const handlePrev = useCallback(() => {
    if (activeStep === 0 && activeWorkflow) {
      const currentIdx = workflowFlows.findIndex((fl) => fl.id === activeFlowId);
      const prevFlow = workflowFlows[currentIdx - 1];
      if (prevFlow) {
        setActiveFlowId(prevFlow.id);
        setActiveStep(Math.max(0, prevFlow.steps.length - 1));
        return;
      }
    }
    setActiveStep((current) => Math.max(0, current - 1));
  }, [activeFlowId, activeStep, activeWorkflow, workflowFlows]);

  const handleNext = useCallback(() => {
    if (!activeFlow || !activeFlowId || !step) {
      return;
    }

    const nextCompletedStepSet = completedStepSet.has(step.id)
      ? completedStepSet
      : new Set([...completedStepSet, step.id]);

    markStepComplete(step.id);

    if (activeStep >= activeFlow.steps.length - 1) {
      markFlowComplete(activeFlowId);

      if (activeWorkflow) {
        const currentIdx = workflowFlows.findIndex((fl) => fl.id === activeFlowId);
        const nextFlow = workflowFlows[currentIdx + 1];
        if (nextFlow) {
          setActiveFlowId(nextFlow.id);
          setActiveStep(getFlowResumeStep(nextFlow, nextCompletedStepSet));
          return;
        }

        markWizardComplete();
      }

      handleBackToMenu();
      return;
    }

    const nextStep = activeFlow.steps[activeStep + 1];
    if (!nextStep || !areStepPrerequisitesMet(nextStep, nextCompletedStepSet)) {
      return;
    }

    setActiveStep((current) => Math.min(activeFlow.steps.length - 1, current + 1));
  }, [activeFlow, activeFlowId, activeStep, activeWorkflow, completedStepSet, handleBackToMenu, markFlowComplete, markStepComplete, markWizardComplete, step, workflowFlows]);

  const handleSelectStep = useCallback((index: number) => {
    if (activeWorkflow) {
      let offset = 0;
      for (const fl of workflowFlows) {
        if (index < offset + fl.steps.length) {
          const targetStep = fl.steps[index - offset];
          if (!targetStep || !isStepSelectable(targetStep, completedStepSet)) {
            return;
          }

          setActiveFlowId(fl.id);
          setActiveStep(index - offset);
          return;
        }
        offset += fl.steps.length;
      }
      return;
    }

    const targetStep = activeFlow?.steps[index];
    if (!targetStep || !isStepSelectable(targetStep, completedStepSet)) {
      return;
    }

    setActiveStep(index);
  }, [activeFlow, activeWorkflow, completedStepSet, workflowFlows]);

  const handleAskSprout = useCallback(
    (flowId?: string) => {
      const defaultPrompt = {
        label: "Show me around SpecTree",
        value: `I'm in the SpecTree Help Center as a ${onboardingStage} user. Can you help me choose the next best guide and explain what to do next?`,
      };
      const prompt = flowId ? (HELP_SUGGESTIONS[flowId] ?? defaultPrompt) : defaultPrompt;

      closeHelpCenter();
      openPanel();
      setPendingSuggestions([
        {
          label: prompt.label,
          value: prompt.value,
          icon: "help-circle",
        },
      ]);
    },
    [closeHelpCenter, onboardingStage, openPanel, setPendingSuggestions]
  );

  const handleToggleDock = useCallback(() => {
    setHelpCenterDockMode(helpCenterDockMode === "modal" ? "right" : "modal");
  }, [helpCenterDockMode, setHelpCenterDockMode]);

  const isDocked = helpCenterDockMode === "right";

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  /* ---- Docked right-panel mode ---- */
  if (isDocked && isHelpCenterOpen) {
    return (
      <AnimatePresence>
        <motion.div
          key="launchpad-docked"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ type: "spring", stiffness: 200, damping: 28, mass: 0.8 }}
          className="fixed top-2 right-2 bottom-2 z-[60] flex w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-background shadow-2xl sm:w-[380px]"
        >
        <TopBar
          mode={activeView}
          docked={isDocked}
          onReturnToLaunchpad={handleBackToMenu}
          onToggleDock={handleToggleDock}
          onClose={handleClose}
        />

        <ScrollContainerContext.Provider value={scrollContainerRef}>
          <div ref={scrollContainerRef} className={cn("launchpad-docked h-full overflow-y-auto pt-14", activeView === "wizard" ? "pb-24" : "pb-12")}>
            {/* Radial hue glow — matches SpecTree page backgrounds */}
            {activeView === "wizard" && activeFlow && (
              <div
                className="pointer-events-none absolute inset-0 z-0"
                style={{
                  background: `radial-gradient(ellipse 140% 50% at 50% -5%, ${activeFlowHue}2B, transparent 70%)`,
                  backgroundColor: `${activeFlowHue}09`,
                  animation: "glow-enter 0.5s ease-out forwards",
                }}
              />
            )}
          <AnimatePresence mode="wait">
            {activeView === "menu" ? (
              <motion.div
                key="launchpad-menu"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={MOTION_TRANSITION}
              >
                <MenuView
                  flows={HELP_FLOWS}
                  completedCount={completedCount}
                  getCompletedSteps={getCompletedSteps}
                  isFlowComplete={isFlowComplete}
                  onSelectFlow={handleSelectFlow}
                  onSelectWorkflow={handleSelectWorkflow}
                  docked
                />
              </motion.div>
            ) : activeFlow && step ? (
              <motion.div
                key={`launchpad-wizard-${activeFlow.id}-${step.id}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={MOTION_TRANSITION}
              >
                <WizardContent
                  flow={activeFlow}
                  step={step}
                  activeStep={activeStep}
                  totalSteps={activeFlow.steps.length}
                  workflowLabel={activeWorkflow ? activeWorkflow.title : undefined}
                  onAdvance={handleNext}
                  onNavigateToFlow={handleSelectFlow}
                  onAskSprout={() => {
                    handleAskSprout(activeFlow.id);
                  }}
                  docked
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
        </ScrollContainerContext.Provider>

        {activeView === "wizard" && activeFlow && step ? (
          <BottomBar
            onBack={activeStep === 0 && (!activeWorkflow || workflowFlows[0]?.id === activeFlowId) ? handleBackToMenu : handlePrev}
            onNext={handleNext}
            onSelectStep={handleSelectStep}
            isLast={activeWorkflow ? isWorkflowLast : isLast}
            totalSteps={activeWorkflow ? workflowTotalSteps : activeFlow.steps.length}
            currentStep={activeWorkflow ? workflowCurrentStep : activeStep}
            flowBreaks={activeWorkflow ? workflowFlowBreaks : undefined}
            stepStates={progressStepStates}
            docked
            />
        ) : null}
        </motion.div>
      </AnimatePresence>
    );
  }

  /* ---- Fullscreen modal mode (default) ---- */
  return (
    <Dialog open={isHelpCenterOpen} onOpenChange={handleOpenChange}>
      <DialogContent overlayClassName="launchpad-overlay" className="launchpad-dialog h-screen w-screen max-h-none max-w-none overflow-hidden rounded-none border-none bg-transparent p-0 shadow-none text-foreground [&>button:last-of-type]:hidden inset-0 translate-x-0 translate-y-0 duration-300">
        <TopBar
          mode={activeView}
          docked={isDocked}
          onReturnToLaunchpad={handleBackToMenu}
          onToggleDock={handleToggleDock}
          onClose={handleClose}
        />

        <ScrollContainerContext.Provider value={scrollContainerRef}>
          <div ref={scrollContainerRef} className={cn("h-full overflow-y-auto pt-16", activeView === "wizard" ? "pb-24" : "pb-12")}>
            {/* Radial hue glow — matches SpecTree page backgrounds */}
            {activeView === "wizard" && activeFlow && (
              <div
                className="pointer-events-none fixed inset-0 z-0"
                style={{
                  background: `radial-gradient(ellipse 140% 50% at 50% -5%, ${activeFlowHue}2B, transparent 70%)`,
                  backgroundColor: `${activeFlowHue}09`,
                  animation: "glow-enter 0.5s ease-out forwards",
                }}
              />
            )}
          <AnimatePresence mode="wait">
            {activeView === "menu" ? (
              <motion.div
                key="launchpad-menu"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={MOTION_TRANSITION}
              >
                <MenuView
                  flows={HELP_FLOWS}
                  completedCount={completedCount}
                  getCompletedSteps={getCompletedSteps}
                  isFlowComplete={isFlowComplete}
                  onSelectFlow={handleSelectFlow}
                  onSelectWorkflow={handleSelectWorkflow}
                />
              </motion.div>
            ) : activeFlow && step ? (
              <motion.div
                key={`launchpad-wizard-${activeFlow.id}-${step.id}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={MOTION_TRANSITION}
              >
                <WizardContent
                  flow={activeFlow}
                  step={step}
                  activeStep={activeStep}
                  totalSteps={activeFlow.steps.length}
                  workflowLabel={activeWorkflow ? activeWorkflow.title : undefined}
                  onAdvance={handleNext}
                  onNavigateToFlow={handleSelectFlow}
                  onAskSprout={() => {
                    handleAskSprout(activeFlow.id);
                  }}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
        </ScrollContainerContext.Provider>

        {activeView === "wizard" && activeFlow && step ? (
          <BottomBar
            onBack={activeStep === 0 && (!activeWorkflow || workflowFlows[0]?.id === activeFlowId) ? handleBackToMenu : handlePrev}
            onNext={handleNext}
            onSelectStep={handleSelectStep}
            isLast={activeWorkflow ? isWorkflowLast : isLast}
            totalSteps={activeWorkflow ? workflowTotalSteps : activeFlow.steps.length}
            currentStep={activeWorkflow ? workflowCurrentStep : activeStep}
            flowBreaks={activeWorkflow ? workflowFlowBreaks : undefined}
            stepStates={progressStepStates}
            />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
